import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { getCategories } from '../../api';
import {
  FiShoppingCart, FiUser, FiMenu, FiX, FiSearch,
  FiLogOut, FiSettings, FiPackage, FiChevronDown, FiTag, FiRefreshCw
} from 'react-icons/fi';
import './Navbar.css';

const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [navCategories, setNavCategories] = useState([]);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    getCategories({ page_size: 10 }).then(({ data }) => {
      setNavCategories(data.results || []);
    }).catch(() => {});
  }, []);

  useEffect(() => { setMobileOpen(false); setUserMenuOpen(false); }, [location]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQ.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQ.trim())}`);
      setSearchOpen(false);
      setSearchQ('');
    }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <header className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="navbar-inner container">
        {/* Logo */}
        <Link to="/" className="navbar-logo">
          <span className="logo-dji">DJI</span>
          <span className="logo-nepal">Nepal</span>
        </Link>

        {/* Nav links */}
        <nav className={`navbar-links ${mobileOpen ? 'open' : ''}`}>
          <Link to="/" className="nav-link">Home</Link>
          <Link to="/products" className="nav-link">All Products</Link>
          <Link to="/second-hand" className="nav-link nav-link-accent">Second Hand</Link>
          <Link to="/blog" className="nav-link">Blog</Link>
          {navCategories.slice(0, 4).map((cat) => (
            <Link key={cat.id} to={`/products?category=${cat.slug}`} className="nav-link">
              {cat.name}
            </Link>
          ))}
        </nav>

        {/* Actions */}
        <div className="navbar-actions">
          {/* Search */}
          {searchOpen ? (
            <form className="search-form" onSubmit={handleSearch}>
              <input
                autoFocus
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Search products..."
              />
              <button type="button" onClick={() => setSearchOpen(false)} className="icon-btn">
                <FiX />
              </button>
            </form>
          ) : (
            <button className="icon-btn" onClick={() => setSearchOpen(true)} title="Search">
              <FiSearch />
            </button>
          )}

          {/* Cart */}
          <Link to="/cart" className="icon-btn cart-btn" title="Cart">
            <FiShoppingCart />
            {count > 0 && <span className="cart-badge">{count}</span>}
          </Link>

          {/* User */}
          {user ? (
            <div className="user-menu-wrap">
              <button className="user-btn" onClick={() => setUserMenuOpen(!userMenuOpen)}>
                <div className="user-avatar">{user.first_name?.[0] || user.username?.[0] || 'U'}</div>
                <FiChevronDown className={`chevron ${userMenuOpen ? 'open' : ''}`} />
              </button>
              {userMenuOpen && (
                <div className="user-dropdown">
                  <div className="dropdown-header">
                    <strong>{user.first_name || user.username}</strong>
                    <small>{user.email}</small>
                  </div>
                  <div className="dropdown-divider" />
                  <Link to="/dashboard" className="dropdown-item"><FiPackage /> My Orders</Link>
                  <Link to="/dashboard?tab=listings" className="dropdown-item"><FiTag /> My Listings</Link>
                  <Link to="/second-hand/sell" className="dropdown-item"><FiRefreshCw /> Sell Second Hand</Link>
                  {isAdmin && <Link to="/admin" className="dropdown-item admin-link"><FiSettings /> Admin Panel</Link>}
                  <div className="dropdown-divider" />
                  <button className="dropdown-item logout" onClick={handleLogout}><FiLogOut /> Sign Out</button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/signin" className="btn btn-primary btn-sm">Sign In</Link>
          )}

          {/* Hamburger */}
          <button className="icon-btn hamburger" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
